/**
 * CSV Parsing utilities for client import
 */

export interface ParsedCSVResult {
  headers: string[];
  rows: string[][];
  separator: string;
  totalRows: number;
}

/**
 * Detect the CSV separator by analyzing the first few lines
 */
export function detectSeparator(text: string): string {
  const firstLines = text.split('\n').slice(0, 5).join('\n');
  const candidates = [';', ',', '\t'];
  
  let bestSep = ',';
  let bestCount = 0;
  
  for (const sep of candidates) {
    const count = (firstLines.match(new RegExp(sep === '\t' ? '\\t' : sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length;
    if (count > bestCount) {
      bestCount = count;
      bestSep = sep;
    }
  }
  
  return bestSep;
}

/**
 * Parse a CSV line respecting quoted fields
 */
function parseCSVLine(line: string, separator: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
        current += '"';
        i++; // skip next quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === separator && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current.trim());
  return result;
}

/**
 * Parse CSV text into headers and rows
 */
export function parseCSV(text: string): ParsedCSVResult {
  // Remove BOM if present
  const cleanText = text.replace(/^\uFEFF/, '');
  
  const separator = detectSeparator(cleanText);
  const lines = cleanText.split(/\r?\n/).filter(line => line.trim().length > 0);
  
  if (lines.length === 0) {
    return { headers: [], rows: [], separator, totalRows: 0 };
  }
  
  const headers = parseCSVLine(lines[0], separator);
  const rows = lines.slice(1).map(line => parseCSVLine(line, separator));
  
  return {
    headers,
    rows,
    separator,
    totalRows: rows.length,
  };
}

/**
 * Known column mappings for auto-detection
 */
export const KNOWN_COLUMN_MAPPINGS: Record<string, string[]> = {
  email: ['email', 'e-mail', 'mail', 'courriel', 'adresse email', 'adresse e-mail', 'email address'],
  firstName: ['prénom', 'prenom', 'first name', 'firstname', 'first_name', 'given name'],
  lastName: ['nom', 'nom de famille', 'last name', 'lastname', 'last_name', 'family name', 'surname'],
  phone: ['téléphone', 'telephone', 'phone', 'tel', 'tél', 'mobile', 'numéro', 'phone number'],
  company: ['société', 'societe', 'entreprise', 'company', 'organisation', 'organization'],
  notes: ['notes', 'commentaires', 'comments', 'remarques', 'observations', 'guest notes'],
  city: ['ville', 'city', 'localité', 'localite'],
  zip: ['code postal', 'cp', 'zip', 'postal code', 'npa'],
  address: ['adresse', 'address', 'rue', 'street'],
  country: ['pays', 'country'],
  civility: ['civilité', 'civilite', 'civility', 'titre', 'title', 'mr/mme'],
  birthdate: ['date de naissance', 'birthdate', 'birthday', 'anniversaire', 'naissance', 'date_of_birth'],
  lang: ['langue', 'language', 'lang'],
  allergiesNotes: ['allergies notes', 'allergies_notes', 'intolérances', 'intolerances', 'dietary', 'allergies'],
  allergiesTags: ['allergies tags', 'allergies_tags', 'allergy tags', 'tags allergies'],
  guestTags: ['guest tags', 'guest_tags', 'étiquettes', 'labels', 'catégories', 'tags'],
  guestStatus: ['guest status', 'guest_status', 'statut client', 'statut', 'status'],
  marketingConsent: ['email optin market', 'marketing consent', 'marketing_consent', 'optin email', 'email optin'],
  smsConsent: ['sms optin market', 'sms consent', 'sms_consent', 'optin sms', 'sms optin'],
  reviewEmailConsent: ['mail optin review', 'review email consent', 'review_email_consent', 'optin review email'],
  reviewSmsConsent: ['sms optin review', 'review sms consent', 'review_sms_consent', 'optin review sms'],
  professionalEmail: ['professional email', 'professional_email', 'email professionnel', 'email pro'],
  professionalPhone: ['professional phone', 'professional_phone', 'téléphone professionnel', 'tel pro', 'phone pro'],
  hasNoShow: ['has no show', 'has_no_show', 'no show', 'no_show'],
  isBlacklisted: ['is blacklisted', 'is_blacklisted', 'blacklisted', 'liste noire', 'blacklisté'],
  importedTotalReservations: ['total reservations', 'nombre de réservations', 'nb reservations', 'reservation count', 'total_reservations', 'nb réservations'],
  importedArrivedReservations: ['arrived reservations', 'réservations arrivées', 'arrived_reservations', 'visites', 'nb arrivées'],
  importedTotalPersons: ['total persons', 'total couverts', 'total_persons', 'nombre couverts', 'total guests', 'nb couverts'],
};

/**
 * Auto-detect column mapping based on header names.
 * Prioritizes exact matches over partial (includes) matches to avoid
 * false positives like "Allergies notes" matching "notes".
 */
export function autoDetectMapping(headers: string[]): Record<number, string> {
  const mapping: Record<number, string> = {};
  const usedFields = new Set<string>();

  // Pass 1: exact matches only
  headers.forEach((header, index) => {
    const normalizedHeader = header.toLowerCase().trim();
    
    for (const [field, aliases] of Object.entries(KNOWN_COLUMN_MAPPINGS)) {
      if (usedFields.has(field)) continue;
      if (aliases.some(alias => normalizedHeader === alias)) {
        mapping[index] = field;
        usedFields.add(field);
        break;
      }
    }
  });

  // Pass 2: partial (includes) matches for remaining unmapped headers
  headers.forEach((header, index) => {
    if (mapping[index]) return; // already mapped
    const normalizedHeader = header.toLowerCase().trim();
    
    for (const [field, aliases] of Object.entries(KNOWN_COLUMN_MAPPINGS)) {
      if (usedFields.has(field)) continue;
      if (aliases.some(alias => normalizedHeader.includes(alias))) {
        mapping[index] = field;
        usedFields.add(field);
        break;
      }
    }
  });
  
  return mapping;
}

/**
 * Target fields available for mapping
 */
export const TARGET_FIELDS = [
  { value: '', label: '— Ignorer —' },
  { value: 'email', label: 'Email' },
  { value: 'firstName', label: 'Prénom' },
  { value: 'lastName', label: 'Nom' },
  { value: 'phone', label: 'Téléphone' },
  { value: 'civility', label: 'Civilité' },
  { value: 'company', label: 'Société' },
  { value: 'notes', label: 'Notes' },
  { value: 'city', label: 'Ville' },
  { value: 'zip', label: 'Code postal' },
  { value: 'address', label: 'Adresse' },
  { value: 'country', label: 'Pays' },
  { value: 'birthdate', label: 'Date de naissance' },
  { value: 'lang', label: 'Langue' },
  { value: 'allergiesNotes', label: 'Allergies (notes)' },
  { value: 'allergiesTags', label: 'Allergies (tags)' },
  { value: 'guestTags', label: 'Tags client' },
  { value: 'guestStatus', label: 'Statut client' },
  { value: 'marketingConsent', label: 'Consentement marketing email' },
  { value: 'smsConsent', label: 'Consentement marketing SMS' },
  { value: 'reviewEmailConsent', label: 'Consentement review email' },
  { value: 'reviewSmsConsent', label: 'Consentement review SMS' },
  { value: 'professionalEmail', label: 'Email professionnel' },
  { value: 'professionalPhone', label: 'Tél. professionnel' },
  { value: 'hasNoShow', label: 'No-show' },
  { value: 'isBlacklisted', label: 'Blacklisté' },
  { value: 'importedTotalReservations', label: 'Nb réservations (historique)' },
  { value: 'importedArrivedReservations', label: 'Nb arrivées (historique)' },
  { value: 'importedTotalPersons', label: 'Nb couverts (historique)' },
] as const;

export type MappableField = typeof TARGET_FIELDS[number]['value'];

/**
 * Validate and normalize a parsed row into a client object
 */
export function mapRowToClient(
  row: string[],
  mapping: Record<number, string>
): Record<string, string> | null {
  const client: Record<string, string> = {};
  
  for (const [indexStr, field] of Object.entries(mapping)) {
    const index = parseInt(indexStr);
    if (field && index < row.length) {
      let value = row[index]?.trim() || '';
      
      // Normalize email
      if (field === 'email') {
        value = value.toLowerCase();
      }
      
      if (value) {
        client[field] = value;
      }
    }
  }
  
  // Email is required
  if (!client.email || !client.email.includes('@')) {
    return null;
  }
  
  return client;
}
